import React, { memo } from 'react';
import { getWeaponDataUri, getIndicatorDataUri } from './weaponIcons';
import { WEAPON_NAMES } from './replayParser';

interface KillFeedItem {
    killerSlot: number;
    victimSlot: number;
    weaponId: number;
    headshot: boolean;
    wallbang: boolean;
    noscope: boolean;
    thrusmoke: boolean;
    frameIdx: number;
    killerName: string;
    victimName: string;
    killerTeam: 'T' | 'CT';
    victimTeam: 'T' | 'CT';
    // Swing overlay: what the kill did to the killer's team's round odds (percentage points);
    // frozen = the round was already decided, so it moved nothing.
    swing?: { delta: number; frozen: boolean };
}

interface ReplayKillFeedProps {
    kills: KillFeedItem[];
    currentFrame: number;
}

const T_COLOR = '#f0c060';
const CT_COLOR = '#80a0d0';

const ReplayKillFeed: React.FC<ReplayKillFeedProps> = ({ kills, currentFrame }) => {
    // Show kills that happened before current frame, last 5, within ~20 seconds (1280 frames)
    const visible = kills
        .filter(k => k.frameIdx <= currentFrame && currentFrame - k.frameIdx < 1280)
        .slice(-5);

    if (visible.length === 0) return null;

    return (
        <div style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            pointerEvents: 'none',
        }}>
            {visible.map((kill, i) => (
                <div key={`${kill.frameIdx}-${kill.victimSlot}`} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    padding: '3px 8px',
                    borderRadius: '3px',
                    fontSize: '11px',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                }}>
                    <span style={{ color: kill.killerTeam === 'T' ? T_COLOR : CT_COLOR }}>
                        {kill.killerName}
                    </span>

                    {getWeaponDataUri(kill.weaponId) ? (
                        <img
                            src={getWeaponDataUri(kill.weaponId)!}
                            alt={WEAPON_NAMES[kill.weaponId] || ''}
                            style={{ height: '12px', width: 'auto', maxWidth: '40px', opacity: 0.9 }}
                        />
                    ) : (
                        <span style={{ color: '#aaa', fontSize: '10px' }}>
                            {WEAPON_NAMES[kill.weaponId] || '?'}
                        </span>
                    )}

                    {kill.headshot && (
                        <img src={getIndicatorDataUri('headshot')} alt="HS" style={{ height: '12px', width: '12px', opacity: 0.9 }} />
                    )}
                    {kill.wallbang && (
                        <img src={getIndicatorDataUri('wallbang')} alt="WB" style={{ height: '12px', width: '12px', opacity: 0.9 }} />
                    )}
                    {kill.noscope && (
                        <img src={getIndicatorDataUri('noscope')} alt="NS" style={{ height: '12px', width: '12px', opacity: 0.9 }} />
                    )}
                    {kill.thrusmoke && (
                        <img src={getIndicatorDataUri('smokeKill')} alt="SM" style={{ height: '12px', width: '12px', opacity: 0.9 }} />
                    )}

                    <span style={{ color: kill.victimTeam === 'T' ? T_COLOR : CT_COLOR }}>
                        {kill.victimName}
                    </span>

                    {kill.swing && (
                        <span style={{
                            marginLeft: '2px',
                            padding: '0 5px',
                            borderRadius: '3px',
                            fontSize: '10px',
                            fontWeight: 800,
                            fontVariantNumeric: 'tabular-nums',
                            ...(kill.swing.frozen
                                ? { color: 'rgba(255,255,255,0.45)', border: '1px solid rgba(255,255,255,0.2)' }
                                : kill.swing.delta >= 0
                                    ? { color: '#4ade80', background: 'rgba(74,222,128,0.14)', border: '1px solid rgba(74,222,128,0.35)' }
                                    : { color: '#f87171', background: 'rgba(248,113,113,0.14)', border: '1px solid rgba(248,113,113,0.35)' }),
                        }}>
                            {kill.swing.frozen ? '–' : `${kill.swing.delta >= 0 ? '+' : '−'}${Math.abs(kill.swing.delta).toFixed(1)}%`}
                        </span>
                    )}
                </div>
            ))}
        </div>
    );
};

export default memo(ReplayKillFeed);
